import {
  type ApiToken,
  ApiToken as ApiTokenSchema,
  ApiTokenList,
  CreateApiTokenResponse,
  type TokenScope,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { Alert, EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { copyText, errorMessage, formatWhen } from '../../lib/messages';

const tokensKey = ['api-tokens'] as const;
const COLS = '2fr 1fr 1.2fr 1.2fr 1fr';
const SCOPE_LABELS: Record<TokenScope, string> = {
  read: 'Só leitura',
  read_write: 'Leitura e escrita',
};

function CreateTokenDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<TokenScope>('read');
  const [touched, setTouched] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/api-tokens',
        CreateApiTokenResponse,
        { name: name.trim(), scope },
        { idempotencyKey: key },
      ),
    onSuccess: (r) => onCreated(r.secret),
  });
  const nameBad = touched && name.trim() === '';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (name.trim() !== '') create.mutate();
  };
  return (
    <Modal
      title="Novo token de API"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="token-form" className="btn p" disabled={create.isPending}>
            Criar token
          </button>
        </>
      }
    >
      <form id="token-form" className="col" style={{ gap: 16 }} onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="tk-n">Nome</label>
          <input
            id="tk-n"
            autoComplete="off"
            className={nameBad ? 'input err' : 'input'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Claude MCP, planilha de vendas"
          />
          {nameBad ? (
            <span className="help err">
              <Icon name="alert" size={14} />
              Dê um nome para saber onde o token é usado.
            </span>
          ) : (
            <span className="help">Aparece no registro de auditoria em cada chamada.</span>
          )}
        </div>
        <div className="field">
          <span className="lbl">Permissão</span>
          <div
            className="seg"
            style={{ alignSelf: 'flex-start' }}
            role="radiogroup"
            aria-label="Permissão"
          >
            {(['read', 'read_write'] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scope === s}
                className={scope === s ? 'on' : ''}
                onClick={() => setScope(s)}
              >
                {SCOPE_LABELS[s]}
              </button>
            ))}
          </div>
          <span className="help">
            {scope === 'read'
              ? 'Pode consultar dados, sem alterar nada.'
              : 'Pode fazer tudo o que o seu papel de Admin permite. Use só em integrações de confiança.'}
          </span>
        </div>
        {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
      </form>
    </Modal>
  );
}

/** One copyable snippet (command or config). */
function Snippet({ label, text }: { label: string; text: string }) {
  const toast = useToast();
  return (
    <div className="col" style={{ gap: 4 }}>
      <span className="lbl">{label}</span>
      <div className="linkbox" style={{ alignItems: 'flex-start' }}>
        <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</code>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            void copyText(text).then((ok) => toast(ok ? 'Copiado' : 'Não deu para copiar'))
          }
        >
          <Icon name="copy" size={16} />
          Copiar
        </button>
      </div>
    </div>
  );
}

/**
 * "Conectar ao Claude": the connector (stdio MCP, apps/mcp) runs on the computer and calls this
 * API with a token. The secret only appears right after creating it.
 */
function ClaudeSetup({ secret }: { secret?: string }) {
  const url = window.location.origin;
  const token = secret ?? 'dz_SEU_TOKEN';
  const path = '/caminho/para/deizi-mcp.mjs';
  return (
    <div className="col" style={{ gap: 10 }}>
      <ol className="sm" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
        <li>
          Crie um token <b>Leitura e escrita</b> para cada pessoa (ex.: “Claude · Lucas”).
        </li>
        <li>
          <a href="/api/v1/mcp/deizi-mcp.mjs" download>
            Baixe o conector
          </a>{' '}
          (precisa do Node 24 no computador) e guarde numa pasta fixa.
        </li>
        <li>
          Cole o comando do Claude Code ou a configuração do Claude Desktop, com o caminho do
          arquivo.
        </li>
      </ol>
      <Snippet
        label="Claude Code (terminal)"
        text={`claude mcp add deizi --scope user -e DEIZI_API_URL=${url} -e DEIZI_API_TOKEN=${token} -- node ${path}`}
      />
      <Snippet
        label="Claude Desktop (claude_desktop_config.json)"
        text={JSON.stringify(
          {
            mcpServers: {
              deizi: {
                command: 'node',
                args: [path],
                env: { DEIZI_API_URL: url, DEIZI_API_TOKEN: token },
              },
            },
          },
          null,
          2,
        )}
      />
      <p className="xs muted">
        O Claude age com as permissões de quem criou o token e tudo fica na auditoria com o nome do
        token. Apagar, anonimizar, usuários e tokens só pela tela.
      </p>
    </div>
  );
}

function SecretDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
  const toast = useToast();
  return (
    <Modal
      title="Token criado"
      onClose={onClose}
      footer={
        <button type="button" className="btn p" onClick={onClose}>
          Já copiei
        </button>
      }
    >
      <Alert tone="warn">
        <b>Copie agora.</b> Por segurança, o token não aparece de novo. Se perder, revogue e crie
        outro.
      </Alert>
      <div className="linkbox">
        <code>{secret}</code>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            void copyText(secret).then((ok) =>
              toast(ok ? 'Token copiado' : 'Não deu para copiar; selecione o texto'),
            )
          }
        >
          <Icon name="copy" size={16} />
          Copiar
        </button>
      </div>
      <p className="xs muted">
        Use no cabeçalho <code>Authorization: Bearer &lt;token&gt;</code>. A documentação da API
        fica em{' '}
        <a href="/api/v1/docs/" target="_blank" rel="noreferrer">
          /api/v1/docs
        </a>
        .
      </p>
      <details>
        <summary className="sm" style={{ cursor: 'pointer', fontWeight: 500 }}>
          Conectar ao Claude com este token
        </summary>
        <div style={{ marginTop: 10 }}>
          <ClaudeSetup secret={secret} />
        </div>
      </details>
    </Modal>
  );
}

function RevokeDialog({ token, onClose }: { token: ApiToken; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const revoke = useMutation({
    mutationFn: () => api.post(`/api-tokens/${token.id}/revoke`, ApiTokenSchema),
    onSuccess: () => {
      toast('Token revogado');
      void qc.invalidateQueries({ queryKey: tokensKey });
      onClose();
    },
  });
  return (
    <Modal
      title={`Revogar “${token.name}”?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn d"
            onClick={() => revoke.mutate()}
            disabled={revoke.isPending}
          >
            Revogar
          </button>
        </>
      }
    >
      <p>O token para de funcionar na hora. Integrações que usam ele vão receber erro de acesso.</p>
      {revoke.isError ? <Alert tone="err">{errorMessage(revoke.error)}</Alert> : null}
    </Modal>
  );
}

/** Admin-only card on the Users page: tokens for integrations and the future MCP server. */
export function ApiTokensSection() {
  const qc = useQueryClient();
  const tokens = useQuery({
    queryKey: tokensKey,
    queryFn: () => api.get('/api-tokens', ApiTokenList),
  });
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const list = tokens.data?.data ?? [];

  return (
    <section className="card flat">
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
        <div>
          <h2>Tokens de API</h2>
          <p className="sm muted">
            Para integrações e para o Claude (Code ou Desktop) trabalhar com os dados da loja.
          </p>
        </div>
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          <Icon name="key" size={18} />
          Novo token
        </button>
      </div>
      {tokens.isPending ? <Skeleton rows={1} /> : null}
      {tokens.isError ? (
        <ErrorBox
          title="Não foi possível carregar os tokens"
          onRetry={() => void tokens.refetch()}
        />
      ) : null}
      {tokens.isSuccess && list.length === 0 ? (
        <EmptyState title="Nenhum token ainda">
          Crie um token quando conectar uma integração.
        </EmptyState>
      ) : null}
      {list.length > 0 ? (
        <div className="gt">
          <div className="gtr head stack-sm" style={{ gridTemplateColumns: COLS }}>
            <span>Nome</span>
            <span>Permissão</span>
            <span>Criado</span>
            <span>Último uso</span>
            <span />
          </div>
          {list.map((t) => (
            <div key={t.id} className="gtr stack-sm" style={{ gridTemplateColumns: COLS }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontWeight: 500 }} className="trunc" title={t.name}>
                  {t.name}
                </p>
                <p className="xs muted">
                  <code>{t.prefix}…</code> · {t.owner.name}
                </p>
              </div>
              <span>
                {t.revokedAt ? (
                  <span className="badge">Revogado</span>
                ) : (
                  <span className={t.scope === 'read' ? 'badge info' : 'badge warn'}>
                    {SCOPE_LABELS[t.scope]}
                  </span>
                )}
              </span>
              <span className="sm tab">{formatWhen(t.createdAt)}</span>
              <span className="sm tab">{t.lastUsedAt ? formatWhen(t.lastUsedAt) : 'nunca'}</span>
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                {!t.revokedAt ? (
                  <button type="button" className="btn sm d" onClick={() => setRevoking(t)}>
                    Revogar
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <details style={{ padding: '10px 18px 16px' }}>
        <summary className="sm" style={{ cursor: 'pointer', fontWeight: 500 }}>
          Conectar ao Claude
        </summary>
        <div style={{ marginTop: 10 }}>
          <ClaudeSetup />
        </div>
      </details>

      {creating ? (
        <CreateTokenDialog
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            setSecret(s);
            void qc.invalidateQueries({ queryKey: tokensKey });
          }}
        />
      ) : null}
      {secret ? <SecretDialog secret={secret} onClose={() => setSecret(null)} /> : null}
      {revoking ? <RevokeDialog token={revoking} onClose={() => setRevoking(null)} /> : null}
    </section>
  );
}
